import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { changeFloorPlanStorey } from '../services/floorplan-editor.service';
import { storeyProblemKey, storeyProblemOf } from '../utils/storey-validation';

interface Props {
  readonly open: boolean;
  readonly floorPlanId: string | undefined;
  /** The floor number the plan has now. */
  readonly storey: number | undefined;
  /** Every floor number the office's plans have, this one's included. */
  readonly storeys: readonly number[];
  readonly onChanged: (storey: number) => void;
  readonly onCancel: () => void;
}

/**
 * Gives the open floorplan another floor number: a plan uploaded as the wrong floor, or a
 * building renumbered.
 *
 * Held to the same rule as a new floor, checked as the number is typed — a whole number, not
 * below the ground floor, not one another plan of the office already has — and by the server
 * again, which also catches a floor another administrator took in the meantime. Only the number
 * changes: the plan, its desks and their bookings stay exactly as they are.
 */
const ChangeStoreyDialog = ({
  open, floorPlanId, storey, storeys, onChanged, onCancel,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setText(storey === undefined ? '' : String(storey));
      setRefusal(undefined);
      setSaving(false);
    }
  }, [open, storey]);

  // Its own number is not taken: keeping it is simply no change.
  const others = storeys.filter((value) => value !== storey);
  const problem = storeyProblemOf(text, others);
  const next = Number(text.trim());
  const unchanged = problem === undefined && next === storey;

  const submit = async (): Promise<void> => {
    if (!floorPlanId || problem || unchanged) return;

    setSaving(true);
    setRefusal(undefined);

    const result = await changeFloorPlanStorey(floorPlanId, next, keycloak).catch((): 'failed' => 'failed');

    setSaving(false);

    if (result === 'changed') {
      onChanged(next);
      return;
    }

    const messages: Record<string, string> = {
      duplicateStorey: String(t('floorplan-storey-taken', { storey: next })),
      invalidStorey: String(t('floorplan-storey-negative')),
    };
    setRefusal(messages[result] ?? String(t('floorplan-editor-change-storey-failed')));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('floorplan-editor-change-storey-title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">{t('floorplan-editor-change-storey-hint')}</Typography>
          {refusal && <Alert severity="error" sx={{ py: 0 }}>{refusal}</Alert>}
          <TextField
            autoFocus
            size="small"
            type="number"
            label={String(t('floorplan-editor-change-storey-label'))}
            value={text}
            onChange={(event): void => {
              setText(event.target.value);
              setRefusal(undefined);
            }}
            onKeyDown={(event): void => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit().catch(() => undefined);
              }
            }}
            inputProps={{ min: 0, step: 1 }}
            error={problem !== undefined}
            helperText={String(problem
              ? t(storeyProblemKey(problem), { storey: text.trim() })
              : t('floorplan-editor-change-storey-current', { storey }))}
            disabled={saving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>{t('cancel')}</Button>
        <Button
          variant="contained"
          onClick={(): void => { submit().catch(() => undefined); }}
          disabled={saving || problem !== undefined || unchanged || !floorPlanId}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          {t('floorplan-editor-change-storey-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeStoreyDialog;
